/**
 * VerificationEmailDialog Component
 * Dialog for initiating verification emails to HR, Universities, Employees
 * Includes preview, document attachments, and CC functionality
 */
import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Mail, Send, Eye, FileText, Paperclip, User, Building2, 
  GraduationCap, Users, Plus, X, RefreshCw, CheckCircle,
  AlertTriangle, Copy, Download, Bell
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RECIPIENT_TYPES = [
  { value: 'hr', label: 'HR / Employer', icon: Building2, color: 'text-blue-600' },
  { value: 'university', label: 'University / College', icon: GraduationCap, color: 'text-purple-600' },
  { value: 'reference', label: 'Reference', icon: Users, color: 'text-amber-600' },
  { value: 'vendor', label: 'Vendor', icon: Building2, color: 'text-slate-600' },
  // Note: Candidate/Employee should NOT receive employment/education verification emails
  // They only receive insufficiency notifications
];

export function VerificationEmailDialog({
  open,
  onOpenChange,
  caseId,
  checkId,
  checkType,
  employeeName,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [activeTab, setActiveTab] = useState('compose');
  
  // Form state
  const [recipientType, setRecipientType] = useState('hr');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [ccEmails, setCcEmails] = useState([]);
  const [newCcEmail, setNewCcEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [customRemarks, setCustomRemarks] = useState('');
  const [useCustomBody, setUseCustomBody] = useState(false);
  const [customBody, setCustomBody] = useState('');
  
  // Documents state
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [includeDeclaration, setIncludeDeclaration] = useState(true);
  
  // Suggestions state
  const [recipientSuggestions, setRecipientSuggestions] = useState([]);
  
  // Preview state
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewData, setPreviewData] = useState(null);
  
  // Email logs
  const [emailLogs, setEmailLogs] = useState([]);

  // Load recipient suggestions and documents on open
  useEffect(() => {
    if (open && caseId && checkId) {
      loadRecipientSuggestions();
      loadAvailableDocuments();
      loadEmailLogs();
    }
  }, [open, caseId, checkId]);

  // Auto-select recipient type based on check type
  useEffect(() => {
    if (checkType) {
      const type = checkType.toLowerCase();
      if (type.includes('education')) {
        setRecipientType('university');
      } else if (type.includes('employment')) {
        setRecipientType('hr');
      } else if (type.includes('reference')) {
        setRecipientType('reference');
      }
    }
  }, [checkType]);

  const loadRecipientSuggestions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/verification-email/recipient-suggestions/${caseId}/${checkId}`);
      setRecipientSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to load recipient suggestions:', error);
    }
  };

  const loadAvailableDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/verification-email/check-documents/${caseId}/${checkId}`);
      setAvailableDocuments(response.data.documents || []);
      // Auto-select declaration form if available
      const declaration = response.data.documents?.find(d => d.type === 'declaration');
      if (declaration) {
        setSelectedDocuments([declaration.id]);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const loadEmailLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/verification-email/logs/${caseId}?check_id=${checkId}`);
      setEmailLogs(response.data || []);
    } catch (error) {
      console.error('Failed to load email logs:', error);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setRecipientEmail(suggestion.email);
    setRecipientName(suggestion.name);
    setRecipientType(suggestion.type);
  };

  const handleAddCc = () => {
    if (newCcEmail && newCcEmail.includes('@') && !ccEmails.includes(newCcEmail)) {
      setCcEmails([...ccEmails, newCcEmail]);
      setNewCcEmail('');
    }
  };

  const handleRemoveCc = (email) => {
    setCcEmails(ccEmails.filter(e => e !== email));
  };

  const handleToggleDocument = (docId) => {
    if (selectedDocuments.includes(docId)) {
      setSelectedDocuments(selectedDocuments.filter(id => id !== docId));
    } else {
      setSelectedDocuments([...selectedDocuments, docId]);
    }
  };

  const handlePreview = async () => {
    if (!recipientEmail) {
      toast.error('Please enter recipient email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/verification-email/preview`, {
        case_id: caseId,
        check_id: checkId,
        recipient_type: recipientType,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        cc_emails: ccEmails,
        subject: subject || null,
        email_body: useCustomBody ? customBody : null,
        include_documents: selectedDocuments,
        custom_remarks: customRemarks || null,
      });
      
      setPreviewData(response.data);
      setPreviewHtml(response.data.html_preview);
      setActiveTab('preview');
      toast.success('Preview generated');
    } catch (error) {
      console.error('Failed to generate preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      toast.error('Please enter recipient email');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await axios.post(`${API_URL}/api/verification-email/send`, {
        case_id: caseId,
        check_id: checkId,
        recipient_type: recipientType,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        cc_emails: ccEmails,
        subject: subject || null,
        email_body: useCustomBody ? customBody : null,
        include_documents: selectedDocuments,
        include_declaration_form: includeDeclaration,
        custom_remarks: customRemarks || null,
      });
      
      toast.success(`Email sent successfully! Tracking ID: ${response.data.tracking_id}`);
      loadEmailLogs();
      setActiveTab('history');
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const getRecipientIcon = (type) => {
    const found = RECIPIENT_TYPES.find(r => r.value === type);
    return found ? found.icon : Mail;
  };

  const handleSendReminder = async (log) => {
    setSendingReminder(log.id);
    try {
      const response = await axios.post(`${API_URL}/api/verification-email/send-reminder`, {
        case_id: log.case_id || caseId,
        check_id: log.check_id || checkId,
        recipient_email: log.recipient_email,
        recipient_name: log.recipient_name,
        cc_emails: log.cc_emails || [],
        include_documents: selectedDocuments,
      });
      toast.success(`Reminder sent! Tracking: ${response.data.tracking_id}`);
      loadEmailLogs();
    } catch (error) {
      console.error('Failed to send reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Initiate Verification Email
            <Badge variant="outline" className="ml-2">{checkType?.replace(/_/g, ' ')}</Badge>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="compose" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Compose
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Preview
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              History ({emailLogs.length})
            </TabsTrigger>
          </TabsList>

          {/* Compose Tab */}
          <TabsContent value="compose" className="space-y-4 mt-4">
            {/* Recipient Suggestions */}
            {recipientSuggestions.length > 0 && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader className="py-2">
                  <CardTitle className="text-sm text-blue-700 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Suggested Recipients
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="flex flex-wrap gap-2">
                    {recipientSuggestions.map((suggestion, idx) => {
                      const Icon = getRecipientIcon(suggestion.type);
                      return (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          onClick={() => handleSelectSuggestion(suggestion)}
                          className={`text-xs ${recipientEmail === suggestion.email ? 'border-blue-500 bg-blue-50' : ''}`}
                        >
                          <Icon className="w-3 h-3 mr-1" />
                          {suggestion.label || suggestion.name}
                          <span className="text-slate-400 ml-1">({suggestion.email})</span>
                        </Button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recipient Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Recipient Type *</Label>
                <Select value={recipientType} onValueChange={setRecipientType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECIPIENT_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${type.color}`} />
                            {type.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Recipient Name</Label>
                <Input
                  placeholder="e.g., HR Department, Registrar"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Recipient Email *</Label>
              <Input
                type="email"
                placeholder="recipient@company.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>

            {/* CC Emails */}
            <div className="space-y-2">
              <Label>CC Recipients</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Add CC email..."
                  value={newCcEmail}
                  onChange={(e) => setNewCcEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCc()}
                  className="flex-1"
                />
                <Button type="button" onClick={handleAddCc} size="sm" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {ccEmails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {ccEmails.map((email, idx) => (
                    <Badge key={idx} variant="secondary" className="pl-2">
                      {email}
                      <button onClick={() => handleRemoveCc(email)} className="ml-1 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Subject (Optional Override) */}
            <div className="space-y-2">
              <Label>Subject (Leave blank for auto-generated)</Label>
              <Input
                placeholder="Custom subject line..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* Custom Remarks */}
            <div className="space-y-2">
              <Label>Additional Remarks</Label>
              <Textarea
                placeholder="Add any additional remarks or instructions..."
                value={customRemarks}
                onChange={(e) => setCustomRemarks(e.target.value)}
                rows={2}
              />
            </div>

            {/* Document Attachments */}
            <Card>
              <CardHeader className="py-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Document Attachments ({selectedDocuments.length} selected)
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                {availableDocuments.length === 0 ? (
                  <p className="text-sm text-slate-500">No documents available for this check</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {availableDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                          selectedDocuments.includes(doc.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300'
                        }`}
                        onClick={() => handleToggleDocument(doc.id)}
                      >
                        <Checkbox
                          checked={selectedDocuments.includes(doc.id)}
                          onCheckedChange={() => handleToggleDocument(doc.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{doc.name}</p>
                          <p className="text-xs text-slate-500">{doc.category}</p>
                        </div>
                        {doc.type === 'declaration' && (
                          <Badge className="bg-green-100 text-green-700 text-xs">Signed</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handlePreview} disabled={loading || !recipientEmail}>
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                  Preview Email
                </Button>
                <Button onClick={handleSendEmail} disabled={sendingEmail || !recipientEmail} className="bg-blue-600 hover:bg-blue-700">
                  {sendingEmail ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Email
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Preview Tab */}
          <TabsContent value="preview" className="mt-4">
            {previewData ? (
              <div className="space-y-4">
                <Card className="bg-slate-50">
                  <CardContent className="py-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-slate-500">From:</span>
                      <span>{previewData.sender_email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-slate-500">To:</span>
                      <span>{previewData.recipient_email}</span>
                    </div>
                    {previewData.cc_emails?.length > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-slate-500">CC:</span>
                        <span>{previewData.cc_emails.join(', ')}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-slate-500">Subject:</span>
                      <span className="font-medium">{previewData.subject}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-slate-500">Tracking ID:</span>
                      <Badge variant="outline">{previewData.tracking_id}</Badge>
                    </div>
                  </CardContent>
                </Card>

                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-3 py-2 border-b">
                    <span className="text-sm font-medium">Email Preview</span>
                  </div>
                  <div 
                    className="p-4 bg-white max-h-[400px] overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
                  />
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setActiveTab('compose')}>
                    Back to Compose
                  </Button>
                  <Button onClick={handleSendEmail} disabled={sendingEmail} className="bg-blue-600 hover:bg-blue-700">
                    {sendingEmail ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Email
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Click "Preview Email" in the Compose tab to see email preview</p>
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-4">
            {emailLogs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No emails sent for this check yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {emailLogs.map((log) => (
                  <Card key={log.id} className={log.reply_received ? 'border-green-300 bg-green-50' : ''}>
                    <CardContent className="py-3">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">{log.tracking_id}</Badge>
                            {log.reply_received ? (
                              <Badge className="bg-green-100 text-green-700 text-xs animate-pulse">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Reply Received
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                Awaiting Reply
                              </Badge>
                            )}
                          </div>
                          <p className="font-medium text-sm">{log.subject}</p>
                          <p className="text-xs text-slate-500">
                            To: {log.recipient_email} ({log.recipient_type})
                          </p>
                          {log.cc_emails?.length > 0 && (
                            <p className="text-xs text-slate-500">CC: {log.cc_emails.join(', ')}</p>
                          )}
                          <p className="text-xs text-slate-400">
                            Sent by: {log.sent_by_agent_name} • {new Date(log.sent_at).toLocaleString()}
                          </p>
                          
                          {/* Reply Details */}
                          {log.reply_received && (
                            <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                              <div className="flex items-center gap-2 mb-2">
                                <Mail className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-medium text-green-700">Reply from {log.reply_from || 'recipient'}</span>
                                {log.reply_received_at && (
                                  <span className="text-xs text-slate-400">
                                    {new Date(log.reply_received_at).toLocaleString()}
                                  </span>
                                )}
                              </div>
                              {log.reply_preview && (
                                <p className="text-sm text-slate-700 bg-green-50 p-2 rounded border-l-4 border-green-400">
                                  "{log.reply_preview}"
                                </p>
                              )}
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="mt-2 text-green-600 border-green-300"
                                onClick={() => {
                                  // Navigate to full reply view
                                  if (log.case_id && log.check_id) {
                                    window.location.href = `/cases/${log.case_id}?check=${log.check_id}&tab=emails`;
                                  }
                                }}
                              >
                                View Full Reply
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 ml-2 flex-col items-end">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              navigator.clipboard.writeText(log.tracking_id);
                              toast.success('Tracking ID copied!');
                            }}
                            title="Copy Tracking ID"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                          {log.recipient_type !== 'reminder' && !log.reply_received && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-amber-600 border-amber-300 hover:bg-amber-50"
                              onClick={() => handleSendReminder(log)}
                              disabled={sendingReminder === log.id}
                              data-testid={`send-reminder-${log.tracking_id}`}
                              title="Send Reminder"
                            >
                              {sendingReminder === log.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <Bell className="w-3 h-3" />
                              )}
                              <span className="ml-1 text-xs">Remind</span>
                            </Button>
                          )}
                          {log.recipient_type === 'reminder' && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              <Bell className="w-3 h-3 mr-1" />
                              Reminder
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export default VerificationEmailDialog;
